import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

import { ApiResult } from '../../../../shared/models/api-result.model';
import {
  VariationAttributeValue,
  CreateVariationAttributeValue,
  UpdateVariationAttributeValue,
} from '../../../models/variation-attribute-value.model';
import { VariationAttributeValueService } from '../../../services/variation-attribute-value.service';
import { VariationAttributeService } from '../../../services/variation-attribute.service';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';
import { DropdownOption } from '../../../../shared/models/dropdowm.model';
import { ToolbarModule } from "primeng/toolbar";


@Component({
  selector: 'app-variation-attribute-value-create-or-update',
  standalone: true,
  templateUrl: './variation-attribute-value-create-or-update.component.html',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    ToggleSwitchModule,
    ProgressSpinnerModule,
    RouterLink,
    FormErrorComponent,
    FormServerErrorComponent,
    ToolbarModule
],
})
export class VariationAttributeValueCreateOrUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private variationAttributeValueService = inject(
    VariationAttributeValueService
  );
  private variationAttributeService = inject(VariationAttributeService);
  private formGroupService = inject(FormGroupService);

  formGroup: FormGroup = this.fb.group({
    id: [null],
    value: [null, [Validators.required, Validators.maxLength(255)]],
    displayName: [null, [Validators.required, Validators.maxLength(255)]],
    extraData: [null, Validators.maxLength(255)],
    sortOrder: [0],
    variationAttributeId: [null, [Validators.required]],
    isActive: [true],
  });

  loading = signal(false);

  variationAttributeValueId = signal<string | null>(null);

  selectLoading = signal(false);

  attributesDropdown = signal<DropdownOption[]>([]);

  ngOnInit(): void {
    this.loadVariationAttributeDropdown();

    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) {
      this.variationAttributeValueId.set(id);
      this.loadData(id);
    }
  }

  loadVariationAttributeDropdown(): void {
    this.selectLoading.set(true);

    this.variationAttributeService.getAllDropdown().subscribe({
      next: (response: ApiResult) => {
        const options = response.data as DropdownOption[];
        this.attributesDropdown.set(options);
        this.selectLoading.set(false);
      },
      error: () => {
        this.selectLoading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Falha',
          detail: 'Ocorreu um erro ao carregar os atributos',
          life: 3000,
        });
      },
    });
  }

  loadData(id: string): void {
    this.loading.set(true);

    this.variationAttributeValueService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const attribute = response.data as VariationAttributeValue;
        this.formGroup.patchValue(attribute);
        this.formGroup.get('variationAttributeId')?.setValue({
          label: attribute.variationAttributeName,
          value: attribute.variationAttributeId,
        });
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar o valor do atributo',
            life: 3000,
          });
        }
      },
    });
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        const control = this.formGroup.get(key);
        control?.markAsTouched();
      });
      return;
    }

    const id = this.variationAttributeValueId();
    const body = this.formGroup.getRawValue();
    body.variationAttributeId = body.variationAttributeId?.value;

    this.loading.set(true);

    let request$: Observable<ApiResult>;

    if (id) {
      body;
      request$ = this.variationAttributeValueService.update(
        id,
        body as UpdateVariationAttributeValue
      );
    } else {
      delete body.id;
      request$ = this.variationAttributeValueService.create(
        body as CreateVariationAttributeValue
      );
    }

    request$.subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Valor de atributo salvo com sucesso',
          life: 2000,
        });

        this.router.navigate(['/variation-attribute-values']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors?.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors?.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors?.length == 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao salvar o valor do atributo',
              life: 3000,
            });
          }
        }
      },
    });
  }
}
